import type { JargonKind, JargonSpan } from "@/lib/tool-kit/study-more/feynman";

const KIND_CLASS: Record<JargonKind, string> = {
  source:
    "bg-amber-200/70 underline decoration-2 decoration-solid dark:bg-amber-400/30",
  plain:
    "bg-rose-200/70 underline decoration-dashed decoration-2 dark:bg-rose-400/30",
  long: "bg-sky-200/70 underline decoration-dotted decoration-2 dark:bg-sky-400/30",
};

interface Segment {
  start: number;
  text: string;
  kind: JargonKind | null;
}

function segments(text: string, spans: readonly JargonSpan[]): Segment[] {
  const parts: Segment[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.start > cursor) {
      parts.push({
        start: cursor,
        text: text.slice(cursor, span.start),
        kind: null,
      });
    }
    parts.push({
      start: span.start,
      text: text.slice(span.start, span.end),
      kind: span.kind,
    });
    cursor = span.end;
  }
  if (cursor < text.length) {
    parts.push({ start: cursor, text: text.slice(cursor), kind: null });
  }
  return parts;
}

/** The explanation with flagged words marked; each kind has its own underline style. */
export function JargonView({
  text,
  spans,
  kindLabels,
  label,
}: {
  text: string;
  spans: readonly JargonSpan[];
  kindLabels: Record<JargonKind, string>;
  label: string;
}) {
  return (
    <section
      aria-label={label}
      className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed"
    >
      {segments(text, spans).map((part) =>
        part.kind ? (
          <mark
            className={`rounded px-0.5 text-foreground ${KIND_CLASS[part.kind]}`}
            key={part.start}
            title={kindLabels[part.kind]}
          >
            {part.text}
          </mark>
        ) : (
          <span key={part.start}>{part.text}</span>
        )
      )}
    </section>
  );
}

/** A small sample of each marking, so colour is never the only cue. */
export function JargonLegend({
  labels,
}: {
  labels: Record<JargonKind, string>;
}) {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-muted-foreground text-xs">
      {(["source", "plain", "long"] as const).map((kind) => (
        <li key={kind}>
          <mark className={`rounded px-1 text-foreground ${KIND_CLASS[kind]}`}>
            {labels[kind]}
          </mark>
        </li>
      ))}
    </ul>
  );
}
